/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  FunctionComponent,
  useCallback,
  useState,
  ChangeEvent,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';

import { SubmitButton } from 'components';
import { Button, FormGroup, Message } from 'components/chroma';
import { Select, OptionGroup } from 'components/chroma/extended';

import { ApiErrorReasonType } from 'scenes/common/types';
import { getApiData, hasData } from 'scenes/common/helpers/apiData';

import analytics from 'scenes/motor/analytics';
import {
  VehicleManualQuoteDetailsField,
  VehicleManualSelectFormValues,
  MotorFullPageProps,
  VehicleSearchType,
} from 'scenes/motor/types';
import { Make } from 'scenes/motor/api/vehicle/types';
import api from 'scenes/motor/api/vehicle';
import { getVehicleManualSelectFields } from 'scenes/motor/selectors';

import VehicleManualSelect from './VehicleManualSelect';
import VehicleNotFoundModal from './VehicleNotFoundModal';
import { itemsToValues } from './service/format';

const LinkWrapper = styled.div`
  margin-top: -1.25rem;
  margin-bottom: 2rem;
`;

const ButtonsWrapper = styled.div`
  text-align: center;
`;

const getMostCommonMakes = (makes: Make[]): string[] =>
  makes.filter((make) => make.popular).map((make) => make.make);

const getOtherManufacturers = (makes: Make[]): string[] =>
  makes.filter((make) => !make.popular).map((make) => make.make);

const orderedQuoteDetailsFields = [
  VehicleManualQuoteDetailsField.VehicleMake,
  VehicleManualQuoteDetailsField.VehicleModel,
  VehicleManualQuoteDetailsField.VehicleManufactureYear,
  VehicleManualQuoteDetailsField.VehicleType,
  VehicleManualQuoteDetailsField.VehicleEngineType,
];

const apiContainer = api();
const VehicleManualStep: FunctionComponent<MotorFullPageProps> = ({
  funnelState,
  referenceData,
  onNext,
  vehicleData,
}) => {
  const { makes } = getApiData(vehicleData);
  const defaultInputFieldValues = getVehicleManualSelectFields(funnelState);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, [setIsModalOpen]);

  const {
    control,
    handleSubmit,
    formState: hookFormState,
    getValues,
    setValue,
    watch,
  } = useForm<VehicleManualSelectFormValues>({
    mode: 'onChange',
    defaultValues: defaultInputFieldValues,
    shouldUnregister: false,
  });

  const watchedValues = watch();
  const clearSubsequentValues = useCallback(
    (fieldName: VehicleManualQuoteDetailsField) => {
      const fieldNameIndex = orderedQuoteDetailsFields.findIndex(
        (item) => item === fieldName
      );

      const subsequentQuoteDetailsFields = orderedQuoteDetailsFields.slice(
        fieldNameIndex + 1
      );

      subsequentQuoteDetailsFields.forEach((name) => {
        setValue(name, '');
      });
    },
    [setValue]
  );

  const shouldDisplayModel = Boolean(
    watchedValues[VehicleManualQuoteDetailsField.VehicleMake]
  );

  const shouldDisplayManufactureYear = Boolean(
    shouldDisplayModel &&
      watchedValues[VehicleManualQuoteDetailsField.VehicleModel]
  );

  const shouldDisplayType = Boolean(
    shouldDisplayManufactureYear &&
      watchedValues[VehicleManualQuoteDetailsField.VehicleManufactureYear]
  );

  const shouldDisplayEngineType = Boolean(
    shouldDisplayType &&
      watchedValues[VehicleManualQuoteDetailsField.VehicleType]
  );

  const getModels = useCallback(
    () =>
      apiContainer.getModels(
        watchedValues[VehicleManualQuoteDetailsField.VehicleMake]
      ),
    [watchedValues[VehicleManualQuoteDetailsField.VehicleMake]]
  );

  const getManufactureYears = useCallback(
    () =>
      apiContainer.getYears(
        watchedValues[VehicleManualQuoteDetailsField.VehicleMake],
        watchedValues[VehicleManualQuoteDetailsField.VehicleModel]
      ),

    [
      watchedValues[VehicleManualQuoteDetailsField.VehicleMake],
      watchedValues[VehicleManualQuoteDetailsField.VehicleModel],
    ]
  );

  const getVehicleTypes = useCallback(
    () =>
      apiContainer.getTypes(
        watchedValues[VehicleManualQuoteDetailsField.VehicleMake],
        watchedValues[VehicleManualQuoteDetailsField.VehicleModel],
        watchedValues[VehicleManualQuoteDetailsField.VehicleManufactureYear]
      ),
    [
      watchedValues[VehicleManualQuoteDetailsField.VehicleMake],
      watchedValues[VehicleManualQuoteDetailsField.VehicleModel],
      watchedValues[VehicleManualQuoteDetailsField.VehicleManufactureYear],
    ]
  );

  const getEngineTypes = useCallback(
    () =>
      apiContainer.getEngineTypes(
        watchedValues[VehicleManualQuoteDetailsField.VehicleMake],
        watchedValues[VehicleManualQuoteDetailsField.VehicleModel],
        watchedValues[VehicleManualQuoteDetailsField.VehicleManufactureYear],
        watchedValues[VehicleManualQuoteDetailsField.VehicleType]
      ),
    [
      watchedValues[VehicleManualQuoteDetailsField.VehicleMake],
      watchedValues[VehicleManualQuoteDetailsField.VehicleModel],
      watchedValues[VehicleManualQuoteDetailsField.VehicleManufactureYear],
      watchedValues[VehicleManualQuoteDetailsField.VehicleType],
    ]
  );

  const onSubmit = handleSubmit((values) => {
    apiContainer
      .getVehicle(
        values.vehicleMake,
        values.vehicleModel,
        values.vehicleManufactureYear,
        values.vehicleType,
        values.vehicleEngineType
      )
      .then((vehicle) => {
        if (hasData(vehicle)) {
          analytics().manualSearch(
            funnelState,
            getApiData(vehicle),
            getApiData(referenceData)
          );
          onNext({
            vehicleManuallyEntered: getApiData(vehicle),
            vehicleManualSelectFields: getValues(),
          });
        }
      });
  });

  return (
    <form onSubmit={onSubmit}>
      <FormGroup
        label="What is the make of the car?"
        id={VehicleManualQuoteDetailsField.VehicleMake}
      >
        <Controller
          name={VehicleManualQuoteDetailsField.VehicleMake}
          defaultValue={
            defaultInputFieldValues
              ? defaultInputFieldValues[
                  VehicleManualQuoteDetailsField.VehicleMake
                ]
              : ''
          }
          rules={{ required: true }}
          control={control}
          render={({ onChange, value }) => (
            <Select
              id={VehicleManualQuoteDetailsField.VehicleMake}
              fieldName={VehicleManualQuoteDetailsField.VehicleMake}
              value={value}
              onChange={(_: ChangeEvent, selected: string) => {
                onChange(selected);
                clearSubsequentValues(
                  VehicleManualQuoteDetailsField.VehicleMake
                );
              }}
              required
              valid={Boolean(value)}
              isLoading={false}
            >
              <option value="" disabled>
                Select
              </option>
              <OptionGroup
                label="Most common"
                optionList={itemsToValues(getMostCommonMakes(makes))}
              />
              <OptionGroup
                label="Other manufacturers"
                optionList={itemsToValues(getOtherManufacturers(makes))}
              />
            </Select>
          )}
        />
      </FormGroup>
      {shouldDisplayModel && (
        <VehicleManualSelect
          funnelState={funnelState}
          fieldName={VehicleManualQuoteDetailsField.VehicleModel}
          getItems={getModels}
          afterOnChange={clearSubsequentValues}
          label="Model"
          control={control}
          defaultValue={
            defaultInputFieldValues
              ? defaultInputFieldValues[
                  VehicleManualQuoteDetailsField.VehicleModel
                ]
              : ''
          }
        />
      )}
      {shouldDisplayManufactureYear && (
        <VehicleManualSelect
          funnelState={funnelState}
          fieldName={VehicleManualQuoteDetailsField.VehicleManufactureYear}
          getItems={getManufactureYears}
          afterOnChange={clearSubsequentValues}
          label="Year of manufacture"
          control={control}
          defaultValue={
            defaultInputFieldValues
              ? defaultInputFieldValues[
                  VehicleManualQuoteDetailsField.VehicleManufactureYear
                ]
              : ''
          }
        />
      )}
      {shouldDisplayType && (
        <VehicleManualSelect
          funnelState={funnelState}
          fieldName={VehicleManualQuoteDetailsField.VehicleType}
          getItems={getVehicleTypes}
          afterOnChange={clearSubsequentValues}
          label="Type of vehicle"
          control={control}
          defaultValue={
            defaultInputFieldValues
              ? defaultInputFieldValues[
                  VehicleManualQuoteDetailsField.VehicleType
                ]
              : ''
          }
        />
      )}
      {shouldDisplayEngineType && (
        <VehicleManualSelect
          funnelState={funnelState}
          fieldName={VehicleManualQuoteDetailsField.VehicleEngineType}
          getItems={getEngineTypes}
          afterOnChange={clearSubsequentValues}
          label="Type of engine"
          control={control}
          defaultValue={
            defaultInputFieldValues
              ? defaultInputFieldValues[
                  VehicleManualQuoteDetailsField.VehicleEngineType
                ]
              : ''
          }
        />
      )}
      <VehicleNotFoundModal
        isOpen={isModalOpen}
        onClose={toggleModal}
        onClickCallUs={() => analytics().callUs(funnelState)}
      />
      <LinkWrapper>
        <Message info link>
          <Button
            link
            onClick={() => {
              analytics().carNotAvailable(funnelState);
              toggleModal();
            }}
          >
            Can&#39;t find the car?
          </Button>
        </Message>
      </LinkWrapper>

      <SubmitButton enabled={hookFormState.isValid}>Next</SubmitButton>

      <ButtonsWrapper>
        {funnelState.apiResponses.foundVehicle.errorReason?.type !==
          ApiErrorReasonType.ApiLimitReached && (
          <Button
            secondary
            maxWidth
            margin={{ top: 3 }}
            onClick={() =>
              onNext({ vehicleSearchType: VehicleSearchType.RegoLookup })
            }
          >
            Find using registration
          </Button>
        )}
      </ButtonsWrapper>
    </form>
  );
};

export default VehicleManualStep;
